export const GRIDFLOW_AUDIO_STORAGE_KEY = "gridflow.audio.v1";

export type AudioCue = "step" | "path-complete" | "level-complete" | "crossing-erase" | "first-win";

export interface AudioEvent {
  readonly cue: AudioCue;
  readonly stepIndex: number | null;
  readonly frequencyHz: number;
}

export interface AudioSnapshot {
  readonly enabled: boolean;
  readonly musicEnabled: boolean;
  readonly lastCue: AudioCue | null;
  readonly playedCueCount: number;
  readonly lastStepIndex: number | null;
  readonly lastFrequencyHz: number | null;
}

export interface AudioStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface AudioService {
  isEnabled(): boolean;
  setEnabled(enabled: boolean): void;
  toggleEnabled(): boolean;
  isMusicEnabled(): boolean;
  setMusicEnabled(enabled: boolean): void;
  toggleMusicEnabled(): boolean;
  playStep(stepIndex: number): void;
  playPathComplete(): void;
  playLevelComplete(): void;
  playCrossingErase(): void;
  playFirstWin(): void;
  getSnapshot(): AudioSnapshot;
  destroy?(): void;
}

export interface AudioServiceOptions {
  readonly enabled?: boolean;
  readonly musicEnabled?: boolean;
  readonly storage?: AudioStorage | null;
  readonly storageKey?: string;
}

interface SerializedAudioSettings {
  readonly enabled?: unknown;
  readonly musicEnabled?: unknown;
}

interface ResolvedAudioSettings {
  readonly enabled: boolean;
  readonly musicEnabled: boolean;
}

const STEP_SCALE_INTERVALS = [0, 2, 4, 5, 7, 9, 11, 12] as const;
const STEP_BASE_FREQUENCY_HZ = 523.25;
const CROSSING_ERASE_FREQUENCY_HZ = 392;
const PATH_COMPLETE_FREQUENCY_HZ = 880;
const LEVEL_COMPLETE_FREQUENCY_HZ = 1174.66;
const FIRST_WIN_FREQUENCY_HZ = 1318.51;

export function calculateStepFrequency(stepIndex: number): number {
  const safeIndex = Math.max(0, Math.floor(Number.isFinite(stepIndex) ? stepIndex : 0));
  const octave = Math.floor(safeIndex / STEP_SCALE_INTERVALS.length);
  const interval = STEP_SCALE_INTERVALS[safeIndex % STEP_SCALE_INTERVALS.length] ?? 0;
  return roundFrequency(STEP_BASE_FREQUENCY_HZ * 2 ** ((interval + octave * 12) / 12));
}

export class MockAudioService implements AudioService {
  public readonly events: AudioEvent[] = [];
  private enabled: boolean;
  private musicEnabled: boolean;

  public constructor(private readonly options: AudioServiceOptions = {}) {
    const settings = readAudioSettings(options);
    this.enabled = settings.enabled;
    this.musicEnabled = settings.musicEnabled;
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    writeAudioSettings(this.options, { enabled });
  }

  public toggleEnabled(): boolean {
    const next = !this.enabled;
    this.setEnabled(next);
    return next;
  }

  public isMusicEnabled(): boolean {
    return this.musicEnabled;
  }

  public setMusicEnabled(enabled: boolean): void {
    this.musicEnabled = enabled;
    writeAudioSettings(this.options, { musicEnabled: enabled });
  }

  public toggleMusicEnabled(): boolean {
    const next = !this.musicEnabled;
    this.setMusicEnabled(next);
    return next;
  }

  public playStep(stepIndex: number): void {
    this.record({
      cue: "step",
      stepIndex: Math.max(0, Math.floor(Number.isFinite(stepIndex) ? stepIndex : 0)),
      frequencyHz: calculateStepFrequency(stepIndex)
    });
  }

  public playPathComplete(): void {
    this.record({
      cue: "path-complete",
      stepIndex: null,
      frequencyHz: PATH_COMPLETE_FREQUENCY_HZ
    });
  }

  public playLevelComplete(): void {
    this.record({
      cue: "level-complete",
      stepIndex: null,
      frequencyHz: LEVEL_COMPLETE_FREQUENCY_HZ
    });
  }

  public playCrossingErase(): void {
    this.record({
      cue: "crossing-erase",
      stepIndex: null,
      frequencyHz: CROSSING_ERASE_FREQUENCY_HZ
    });
  }

  public playFirstWin(): void {
    this.record({
      cue: "first-win",
      stepIndex: null,
      frequencyHz: FIRST_WIN_FREQUENCY_HZ
    });
  }

  public getSnapshot(): AudioSnapshot {
    const lastEvent = this.events.at(-1);
    return {
      enabled: this.enabled,
      musicEnabled: this.musicEnabled,
      lastCue: lastEvent?.cue ?? null,
      playedCueCount: this.events.length,
      lastStepIndex: lastEvent?.stepIndex ?? null,
      lastFrequencyHz: lastEvent?.frequencyHz ?? null
    };
  }

  private record(event: AudioEvent): void {
    if (!this.enabled) {
      return;
    }
    this.events.push(event);
  }
}

export class WebAudioService implements AudioService {
  private enabled: boolean;
  private musicEnabled: boolean;
  private context: AudioContext | null = null;
  private musicGain: GainNode | null = null;
  private readonly musicOscillators: OscillatorNode[] = [];
  private lastEvent: AudioEvent | null = null;
  private playedCueCount = 0;

  public constructor(private readonly options: AudioServiceOptions = {}) {
    const settings = readAudioSettings(options);
    this.enabled = settings.enabled;
    this.musicEnabled = settings.musicEnabled;
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    writeAudioSettings(this.options, { enabled });
  }

  public toggleEnabled(): boolean {
    const next = !this.enabled;
    this.setEnabled(next);
    return next;
  }

  public isMusicEnabled(): boolean {
    return this.musicEnabled;
  }

  public setMusicEnabled(enabled: boolean): void {
    this.musicEnabled = enabled;
    writeAudioSettings(this.options, { musicEnabled: enabled });
    if (enabled) {
      void this.startMusic();
    } else {
      this.stopMusic();
    }
  }

  public toggleMusicEnabled(): boolean {
    const next = !this.musicEnabled;
    this.setMusicEnabled(next);
    return next;
  }

  public playStep(stepIndex: number): void {
    this.playTone({
      cue: "step",
      stepIndex: Math.max(0, Math.floor(Number.isFinite(stepIndex) ? stepIndex : 0)),
      frequencyHz: calculateStepFrequency(stepIndex)
    });
  }

  public playPathComplete(): void {
    this.playTone({
      cue: "path-complete",
      stepIndex: null,
      frequencyHz: PATH_COMPLETE_FREQUENCY_HZ
    });
  }

  public playLevelComplete(): void {
    this.playTone({
      cue: "level-complete",
      stepIndex: null,
      frequencyHz: LEVEL_COMPLETE_FREQUENCY_HZ
    });
  }

  public playCrossingErase(): void {
    this.playTone({
      cue: "crossing-erase",
      stepIndex: null,
      frequencyHz: CROSSING_ERASE_FREQUENCY_HZ
    });
  }

  public playFirstWin(): void {
    this.playTone({
      cue: "first-win",
      stepIndex: null,
      frequencyHz: FIRST_WIN_FREQUENCY_HZ
    });
  }

  public getSnapshot(): AudioSnapshot {
    return {
      enabled: this.enabled,
      musicEnabled: this.musicEnabled,
      lastCue: this.lastEvent?.cue ?? null,
      playedCueCount: this.playedCueCount,
      lastStepIndex: this.lastEvent?.stepIndex ?? null,
      lastFrequencyHz: this.lastEvent?.frequencyHz ?? null
    };
  }

  public destroy(): void {
    this.stopMusic();
    void this.context?.close();
    this.context = null;
  }

  private playTone(event: AudioEvent): void {
    if (!this.enabled) {
      return;
    }
    this.lastEvent = event;
    this.playedCueCount += 1;

    const context = this.ensureContext();
    if (!context) {
      return;
    }
    if (context.state === "suspended") {
      void context.resume();
    }
    if (this.musicEnabled) {
      void this.startMusic();
    }

    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const now = context.currentTime;
    const duration = event.cue === "step" ? 0.07 : event.cue === "crossing-erase" ? 0.09 : 0.18;
    const peakGain =
      event.cue === "level-complete" || event.cue === "first-win"
        ? 0.045
        : event.cue === "crossing-erase"
          ? 0.026
          : 0.032;

    oscillator.type = event.cue === "step" ? "sine" : "triangle";
    oscillator.frequency.setValueAtTime(event.frequencyHz, now);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(peakGain, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.02);
  }

  private async startMusic(): Promise<void> {
    if (!this.musicEnabled || this.musicGain || this.musicOscillators.length > 0) {
      return;
    }
    const context = this.ensureContext();
    if (!context) {
      return;
    }
    if (context.state === "suspended") {
      await context.resume();
    }

    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.006, context.currentTime + 0.8);
    gain.connect(context.destination);

    const root = context.createOscillator();
    root.type = "sine";
    root.frequency.setValueAtTime(130.81, context.currentTime);
    root.connect(gain);
    root.start();

    const fifth = context.createOscillator();
    fifth.type = "triangle";
    fifth.frequency.setValueAtTime(196, context.currentTime);
    fifth.connect(gain);
    fifth.start();

    this.musicGain = gain;
    this.musicOscillators.push(root, fifth);
  }

  private stopMusic(): void {
    if (this.musicGain) {
      try {
        this.musicGain.gain.setValueAtTime(0.0001, this.musicGain.context.currentTime);
      } catch {
        // Web Audio nodes can throw after context shutdown; stopping remains best-effort.
      }
      this.musicGain.disconnect();
      this.musicGain = null;
    }
    for (const oscillator of this.musicOscillators.splice(0)) {
      try {
        oscillator.stop();
      } catch {
        continue;
      }
      oscillator.disconnect();
    }
  }

  private ensureContext(): AudioContext | null {
    if (this.context) {
      return this.context;
    }
    const AudioContextCtor =
      globalThis.AudioContext ??
      (globalThis as typeof globalThis & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioContextCtor) {
      return null;
    }
    this.context = new AudioContextCtor();
    return this.context;
  }
}

export function createMockAudioService(options?: AudioServiceOptions): AudioService {
  return new MockAudioService(options);
}

export function createBrowserAudioService(options: AudioServiceOptions = {}): AudioService {
  return new WebAudioService({
    storage: safeLocalStorage(),
    ...options
  });
}

export function createMemoryAudioStorage(
  seed: Record<string, string> = {}
): AudioStorage & { readonly snapshot: Record<string, string> } {
  const values = { ...seed };
  return {
    snapshot: values,
    getItem(key: string) {
      return Object.prototype.hasOwnProperty.call(values, key) ? (values[key] ?? null) : null;
    },
    setItem(key: string, value: string) {
      values[key] = value;
    },
    removeItem(key: string) {
      Reflect.deleteProperty(values, key);
    }
  };
}

function readAudioSettings(options: AudioServiceOptions): ResolvedAudioSettings {
  const stored = readStoredAudioSettings(options);
  const storedEnabled = typeof stored.enabled === "boolean" ? stored.enabled : true;
  const storedMusicEnabled = typeof stored.musicEnabled === "boolean" ? stored.musicEnabled : true;
  return {
    enabled: options.enabled ?? storedEnabled,
    musicEnabled: options.musicEnabled ?? storedMusicEnabled
  };
}

function readStoredAudioSettings(options: AudioServiceOptions): SerializedAudioSettings {
  const key = options.storageKey ?? GRIDFLOW_AUDIO_STORAGE_KEY;
  try {
    const raw = options.storage?.getItem(key);
    if (!raw) {
      return {};
    }
    return JSON.parse(raw) as SerializedAudioSettings;
  } catch {
    return {};
  }
}

function writeAudioSettings(
  options: AudioServiceOptions,
  patch: Partial<ResolvedAudioSettings>
): void {
  const key = options.storageKey ?? GRIDFLOW_AUDIO_STORAGE_KEY;
  try {
    const previous = readStoredAudioSettings(options);
    const enabled = typeof previous.enabled === "boolean" ? previous.enabled : true;
    const musicEnabled = typeof previous.musicEnabled === "boolean" ? previous.musicEnabled : true;
    options.storage?.setItem(
      key,
      JSON.stringify({
        enabled,
        musicEnabled,
        ...patch
      })
    );
  } catch {
    return;
  }
}

function safeLocalStorage(): AudioStorage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function roundFrequency(value: number): number {
  return Math.round(value * 100) / 100;
}
